// src/pages/AppSettingsPage.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from './AppSettingsPage.module.css';
import apiClient from '../services/ApiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';

const AppSettingsPage = () => {
  const [settings, setSettings] = useState([]);
  const [editValues, setEditValues] = useState({});
  // savingKeys: yarım kalan ayar-düzenleme özelliği için (bkz. handleSave TODO notu).
  // eslint-disable-next-line no-unused-vars
  const [savingKeys, setSavingKeys] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [refillId, setRefillId] = useState('');
  const [isDeletingRefill, setIsDeletingRefill] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/v1/admin/settings');
      const data = response.data?.data || [];
      setSettings(data);
      const initialValues = {};
      data.forEach(s => {
        initialValues[s.key] = s.value;
      });
      setEditValues(initialValues);
    } catch (error) {
      console.error('Ayarlar yüklenirken hata:', error);
      toast.error('Ayarlar yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // TODO (teslim): Bu sayfa şu an ayarları SALT OKUNUR gösteriyor. Düzenleme mantığı
  // (handleValueChange + handleSave) hazır ancak her satıra input alanı ve "Kaydet"
  // butonu JSX'i eklenmediği için kullanılmıyor. Aşağıdaki settings.map içine input +
  // buton eklenerek düzenleme özelliği tamamlanmalı.
  // eslint-disable-next-line no-unused-vars
  const handleValueChange = (key, value) => {
    setEditValues(prev => ({ ...prev, [key]: value }));
  };

  // eslint-disable-next-line no-unused-vars
  const handleSave = async (key) => {
    const value = editValues[key];
    setSavingKeys(prev => new Set(prev).add(key));
    try {
      const response = await apiClient.patch(`/v1/admin/settings/${key}`, { value });
      const updatedSetting = response.data?.data;
      if (updatedSetting) {
        setSettings(prev =>
          prev.map(s => s.key === key ? { ...s, value: updatedSetting.value } : s)
        );
        setEditValues(prev => ({ ...prev, [key]: updatedSetting.value }));
      }
      toast.success('Ayar başarıyla güncellendi');
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Ayar güncellenirken bir hata oluştu';
      toast.error(errorMsg);
    } finally {
      setSavingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const isValueChanged = (setting) => {
    return editValues[setting.key] !== setting.value;
  };

  const handleDeleteRefill = async () => {
    const id = refillId.trim();
    if (!id) {
      toast.error('Lütfen bir ürün (refill) ID girin');
      return;
    }
    const confirmed = window.confirm(
      `"${id}" ID'li ürün Google Play'den silinecek. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?`
    );
    if (!confirmed) return;

    setIsDeletingRefill(true);
    try {
      await apiClient.delete(`/esim-packages/refills/${encodeURIComponent(id)}/google-play`);
      toast.success(`"${id}" ID'li ürün Google Play'den silindi`);
      setRefillId('');
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Ürün silinirken bir hata oluştu';
      toast.error(errorMsg);
    } finally {
      setIsDeletingRefill(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingSpinner />
        <p>Uygulama ayarları yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>Uygulama Ayarları</h1>
            {settings.length > 0 && (
              <span className={`${styles.countChip} num`}>{settings.length}</span>
            )}
          </div>
          <p className={styles.description}>
            Sistemin genel davranışını etkileyen ayarları görüntüleyin ve düzenleyin.
          </p>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.dangerCard}>
          <div className={styles.dangerHeader}>
            <h2 className={styles.dangerTitle}>Google Play Ürün Silme</h2>
            <p className={styles.dangerDescription}>
              Silmek istediğiniz ürünün (refill) ID'sini girin. Ürün Google Play'den kalıcı
              olarak silinecektir. Bu işlem geri alınamaz.
            </p>
          </div>
          <div className={styles.dangerForm}>
            <input
              type="text"
              className={styles.valueInput}
              placeholder="Ürün (refill) ID — örn. 22175"
              value={refillId}
              onChange={(e) => setRefillId(e.target.value)}
              disabled={isDeletingRefill}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isDeletingRefill) handleDeleteRefill();
              }}
            />
            <button
              type="button"
              className={styles.deleteButton}
              onClick={handleDeleteRefill}
              disabled={isDeletingRefill || !refillId.trim()}
            >
              {isDeletingRefill ? 'Siliniyor...' : 'Google Play\'den Sil'}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        {settings.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Henüz hiç ayar bulunamadı.</p>
          </div>
        ) : (
          <div className={styles.settingsList}>
            {settings.map(setting => {
              // NOT: input + Kaydet butonu eklendiğinde şu değerler kullanılacak:
              //   savingKeys.has(setting.key), editValues[setting.key], isValueChanged(setting)
              return (
                <div key={setting.key} className={styles.settingRow}>
                  <div className={styles.settingMeta}>
                    <div className={styles.settingKeyRow}>
                      <span className={styles.settingKey}>{setting.key}</span>
                      {setting.editable ? (
                        <span className={`${styles.badge} ${styles.badgeEditable}`}>Düzenlenebilir</span>
                      ) : (
                        <span className={`${styles.badge} ${styles.badgeReadOnly}`}>Salt Okunur</span>
                      )}
                    </div>
                    {setting.description && (
                      <p className={styles.settingDescription}>{setting.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppSettingsPage;
